/**
 * useBackgroundAuth - Background Pi Authentication Hook
 *
 * Fully self-contained auth lifecycle for the home screen.
 * Does NOT depend on useSession or RootLayout's session check.
 *
 * Flow:
 * 1. If token exists -> validate via session API -> update store
 * 2. If token expired -> try refresh (~50ms) -> update store
 * 3. If no token -> wait for Pi SDK -> auto-authenticate -> update store
 * 4. On transient failure -> retry up to 2 times with backoff
 * 5. On permanent failure -> minimize to notch dot, allow manual retry
 *
 * Returns AuthProgress so UI can display a progress indicator,
 * plus a retry() function for user-initiated re-authentication.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  createApiClient,
  getSessionToken,
  setSessionToken,
  setGameToken,
  setTokenExpiry,
  refreshSessionToken,
  startTokenRefreshInterval,
} from '@room/frontend-sdk';
import { useSessionStore, shouldRedirectToLogin, getLoginRedirectUrl, type APIResponse } from '@room/shared-ui';

type AuthResponse = {
  sessionToken: string;
  gameToken?: string;
  userId: string;
  expiresAt?: number;
  user: {
    id: string;
    username: string;
    role: string;
  };
};

export type AuthPhase =
  | 'idle'
  | 'checking'
  | 'refreshing'
  | 'waiting-sdk'
  | 'authenticating'
  | 'verifying'
  | 'done';

export type AuthProgress = {
  phase: AuthPhase;
  progress: number;
  message: string;
  retry: () => void;
};

const MAX_PI_AUTH_RETRIES = 2;
const RETRY_BACKOFF_MS = [2000, 4000]; // 2s, 4s
const PI_AUTHENTICATE_TIMEOUT_MS = 15000; // 15s timeout for Pi.authenticate()

// API client for background auth - must never redirect on 401.
const api = createApiClient({
  baseURL: '/api',
  onUnauthorized: () => {
    // No-op: background auth handles failures silently
  },
  enableAutoRefresh: false, // We handle refresh ourselves
});

const PI_SDK_URL = 'https://sdk.minepi.com/pi-sdk.js';
const PI_SDK_OPTIONS = { version: '2.0', sandbox: false };

/**
 * Redirect to /login for alternative auth methods (Facebook, Guest).
 * Uses shouldRedirectToLogin() to prevent redirect loops.
 * Skips redirect entirely when a bundle app is active or loading via deep link,
 * because redirecting would destroy the running bundle overlay.
 */
function redirectToLoginPage(): void {
  // Never redirect when a bundle app is active or being loaded
  if ((window as any).__ROOM_BUNDLE_MODE__) {
    console.log('[BackgroundAuth] Skipping login redirect — bundle mode active');
    return;
  }

  // Also check for deep link URL (e.g. /cadival) — a bundle may be loading
  const pathname = window.location.pathname;
  const appSegment = pathname.split('/').filter(Boolean)[0];
  if (appSegment && appSegment !== 'home' && appSegment !== 'login') {
    console.log(`[BackgroundAuth] Skipping login redirect — deep link active: /${appSegment}`);
    return;
  }

  const returnTo = pathname + window.location.search;
  if (shouldRedirectToLogin(pathname)) {
    console.log('[BackgroundAuth] Redirecting to login for alternative auth');
    window.location.replace(getLoginRedirectUrl(returnTo));
  }
}

/**
 * Load the Pi SDK script tag if not already present.
 */
function loadPiSdkScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).Pi) {
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-pi-sdk="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Pi SDK script failed to load')));
      return;
    }

    const script = document.createElement('script');
    script.src = PI_SDK_URL;
    script.async = true;
    script.dataset.piSdk = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Pi SDK script failed to load'));
    document.head.appendChild(script);
  });
}

/**
 * Load + initialize Pi SDK, then set __PI_SDK_READY__.
 * Home app's index.html doesn't include the Pi SDK script tag
 * (unlike login/index.html), so we must load it ourselves.
 */
async function ensurePiSdk(timeoutMs = 15000): Promise<boolean> {
  const win = window as any;

  // Already initialized by another flow
  if (win.__PI_SDK_READY__ === true) return true;

  // Already determined this is NOT Pi Browser — don't retry
  if (win.__PI_SDK_READY__ === false) return false;

  try {
    // Race SDK init against timeout
    const result = await Promise.race([
      (async () => {
        // Reuse init promise if someone else started it
        if (!win.__PI_SDK_INIT_PROMISE__) {
          win.__PI_SDK_INIT_PROMISE__ = (async () => {
            await loadPiSdkScript();
            if (!win.Pi?.init) {
              throw new Error('Pi SDK loaded but Pi.init not available');
            }
            await win.Pi.init(PI_SDK_OPTIONS);
            win.__PI_SDK_READY__ = true;
            console.log('[BackgroundAuth] Pi SDK loaded and initialized');
          })();
        }
        await win.__PI_SDK_INIT_PROMISE__;
        return true;
      })(),
      new Promise<false>((resolve) => setTimeout(() => resolve(false), timeoutMs)),
    ]);

    return result;
  } catch (err) {
    console.warn('[BackgroundAuth] Pi SDK init failed:', err);
    win.__PI_SDK_READY__ = false;
    return false;
  }
}

/**
 * Validate session token directly via fetch.
 */
async function validateSession(token: string) {
  try {
    const response = await fetch('/api/auth/session', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data.success && data.data?.user) {
      return data.data.user;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Wait for a specified duration (for retry backoff)
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useBackgroundAuth(): AuthProgress {
  const [authProgress, setAuthProgress] = useState<AuthProgress>({
    phase: 'idle',
    progress: 0,
    message: '',
    retry: () => {},
  });
  const isRunning = useRef(false);
  const mountedRef = useRef(true);

  const attemptAuth = useCallback(async () => {
    if (isRunning.current) return;
    isRunning.current = true;

    const update = (p: Omit<AuthProgress, 'retry'>) => {
      if (mountedRef.current) {
        setAuthProgress((prev) => ({ ...p, retry: prev.retry }));
      }
    };

    const { setSession } = useSessionStore.getState();

    try {
      const token = getSessionToken();

      // Case 1: Token exists -> validate it
      if (token) {
        update({ phase: 'checking', progress: 20, message: 'Verifying session...' });

        const user = await validateSession(token);
        if (user) {
          console.log('[BackgroundAuth] Existing session valid');
          setSession(user);
          update({ phase: 'done', progress: 100, message: 'Signed in' });
          return;
        }

        // Token invalid -> try refresh
        update({ phase: 'refreshing', progress: 40, message: 'Refreshing...' });
        try {
          const newToken = await refreshSessionToken('/api');
          if (newToken) {
            update({ phase: 'verifying', progress: 80, message: 'Almost done...' });
            const refreshedUser = await validateSession(newToken);
            if (refreshedUser) {
              console.log('[BackgroundAuth] Token refreshed successfully');
              setSession(refreshedUser);
              update({ phase: 'done', progress: 100, message: 'Signed in' });
              return;
            }
          }
        } catch {
          // Refresh failed, fall through to Pi SDK
        }
      }

      // Case 2: No token or refresh failed
      // Try loading Pi SDK — if it initializes, we're in Pi Browser.
      // If it fails, redirect to /login for alternative auth (Facebook, Guest).
      // NOTE: user-agent is unreliable for Pi Browser detection.
      //       Pi.init() success is the only reliable signal.
      update({ phase: 'waiting-sdk', progress: 30, message: 'Connecting...' });

      const piReady = await ensurePiSdk(10000);
      if (!piReady || !(window as any).Pi) {
        console.log('[BackgroundAuth] Pi SDK not available, redirecting to login');
        useSessionStore.setState({ hasCheckedSession: true });
        update({ phase: 'idle', progress: 0, message: '' });
        redirectToLoginPage();
        return;
      }

      // Pi authenticate + POST /auth/pi with retry
      let lastError: unknown;
      for (let attempt = 0; attempt <= MAX_PI_AUTH_RETRIES; attempt++) {
        if (attempt > 0) {
          const backoff = RETRY_BACKOFF_MS[attempt - 1] ?? 4000;
          console.log(`[BackgroundAuth] Retry ${attempt}/${MAX_PI_AUTH_RETRIES} after ${backoff}ms...`);
          update({
            phase: 'authenticating',
            progress: 55 + attempt * 5,
            message: `Retrying... (${attempt}/${MAX_PI_AUTH_RETRIES})`,
          });
          await delay(backoff);
        }

        try {
          update({
            phase: 'authenticating',
            progress: attempt === 0 ? 60 : 55 + attempt * 5,
            message: attempt === 0 ? 'Signing in...' : `Retrying... (${attempt}/${MAX_PI_AUTH_RETRIES})`,
          });

          console.log(`[BackgroundAuth] Pi authenticate attempt ${attempt + 1}...`);
          const authResult = await Promise.race([
            (window as any).Pi.authenticate(
              ['username', 'payments', 'wallet_address'],
              () => {},
            ),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Pi.authenticate() timed out')), PI_AUTHENTICATE_TIMEOUT_MS),
            ),
          ]);

          update({ phase: 'verifying', progress: 85, message: 'Almost done...' });
          const response = await api.post<APIResponse<AuthResponse>>('/auth/pi', {
            accessToken: authResult.accessToken,
            user: authResult.user,
          });

          if (!response.success || !response.data?.sessionToken) {
            console.warn('[BackgroundAuth] Pi auth response failed:', response.error);
            lastError = new Error(response.error || 'Auth response invalid');
            continue; // Retry
          }

          // Store token — same as login screen does
          setSessionToken(response.data.sessionToken);
          if (response.data.gameToken) {
            setGameToken(response.data.gameToken);
          }
          if (response.data.expiresAt) {
            setTokenExpiry(response.data.expiresAt);
          }

          // Trust the /auth/pi response directly (login screen does exactly this).
          // No extra validateSession() call — the token was JUST created by the server,
          // calling GET /auth/session again is redundant and can fail due to timing.
          const userData = response.data.user;
          if (userData) {
            setSession(userData);
            console.log('[BackgroundAuth] Pi auto-login completed successfully');
            update({ phase: 'done', progress: 100, message: 'Signed in' });
            return; // Success!
          }

          // Fallback: response didn't include user data, validate via API
          update({ phase: 'verifying', progress: 92, message: 'Finalizing...' });
          const user = await validateSession(response.data.sessionToken);
          if (user) {
            setSession(user);
            console.log('[BackgroundAuth] Session validated successfully');
            update({ phase: 'done', progress: 100, message: 'Signed in' });
            return;
          }

          lastError = new Error('Session validation failed after token creation');
          break;
        } catch (err) {
          lastError = err;
          console.warn(`[BackgroundAuth] Attempt ${attempt + 1} failed:`, err);

          // Don't retry if Pi.authenticate() was rejected/denied/timed out — these won't succeed on retry
          const errMsg = String(err);
          if (errMsg.includes('cancelled') || errMsg.includes('denied') || errMsg.includes('rejected') || errMsg.includes('timed out')) {
            console.log('[BackgroundAuth] Auth cancelled/denied/timed out, not retrying');
            break;
          }
          // Otherwise continue retry loop (network error, timeout, etc.)
        }
      }

      // All retries exhausted — redirect to login for alternative auth (Facebook, Guest)
      console.warn('[BackgroundAuth] All attempts failed, redirecting to login:', lastError);
      useSessionStore.setState({ hasCheckedSession: true });
      update({ phase: 'idle', progress: 0, message: '' });
      redirectToLoginPage();
    } finally {
      isRunning.current = false;
    }
  }, []);

  // Run on mount
  useEffect(() => {
    mountedRef.current = true;
    attemptAuth();
    return () => {
      mountedRef.current = false;
    };
  }, [attemptAuth]);

  // Start proactive token refresh interval once authenticated
  useEffect(() => {
    if (authProgress.phase !== 'done') return;

    const stopRefreshInterval = startTokenRefreshInterval('/api');
    return stopRefreshInterval;
  }, [authProgress.phase]);

  // Wire up retry function
  useEffect(() => {
    setAuthProgress((prev) => ({ ...prev, retry: attemptAuth }));
  }, [attemptAuth]);

  return authProgress;
}
